/**
 * บิลที่กำลังทำอยู่
 *
 * ★ ทำไมต้องเก็บไว้ระดับบนสุด ★
 * แอปเดิมเป็นหน้าจอเดียว สลับหมวดไปมาแล้วตะกร้ายังอยู่ครบ พนักงานเดินไปกด
 * หมวดโน้นหมวดนี้แล้วกลับมาที่เดิมได้ตลอด ถ้าตะกร้าผูกกับหน้าใดหน้าหนึ่ง
 * ของจะหายทุกครั้งที่เปลี่ยนหมวด ซึ่งจะพังขั้นตอนการทำงานทั้งหมด
 *
 * โครงตาม hooks/useBill.js ของแอปเดิม
 *   payload    ข้อมูลหัวบิล
 *   carts      รายการใหม่ที่ยังไม่ได้ส่งครัว
 *   oldOrder   รายการที่ส่งครัวไปแล้ว (มาจากเซิร์ฟเวอร์)
 */

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { api } from './lib/api.ts';
import type {
  BillType,
  CartItem,
  DiscountButton,
  Product,
  SaleDetail,
} from './lib/types.ts';

export interface BillPayload {
  saleId: number | null;
  billNo: string;
  billType: BillType | '';
  diningTableId: number | null;
  tableName: string;
  guestCount: number;
  customerId: number | null;
  customerName: string;
  customerPhone: string;
  memberPct: number;
  deliveryChannelId: number | null;
  deliveryName: string;
  deliveryOrderRef: string;
}

const EMPTY: BillPayload = {
  saleId: null,
  billNo: '',
  billType: '',
  diningTableId: null,
  tableName: '',
  guestCount: 1,
  customerId: null,
  customerName: '',
  customerPhone: '',
  memberPct: 0,
  deliveryChannelId: null,
  deliveryName: '',
  deliveryOrderRef: '',
};

/** ส่วนลดที่กดไว้แต่ยังไม่ได้ส่งขึ้นเซิร์ฟเวอร์ (บิลยังไม่ถูกสร้าง) */
export interface PendingDiscount {
  code: string;
  label: string;
  kind: 'percent' | 'fixed';
  percent: number | null;
  amountSatang: number | null;
}

interface BillStore {
  payload: BillPayload;
  setPayload: (patch: Partial<BillPayload>) => void;
  sale: SaleDetail | null;

  cart: CartItem[];
  addProduct: (product: Product) => void;
  setQty: (index: number, qty: number) => void;
  setNote: (index: number, note: string) => void;
  removeItem: (index: number) => void;

  pendingDiscount: PendingDiscount | null;
  applyDiscountButton: (button: DiscountButton) => void;
  clearPendingDiscount: () => void;

  reset: () => void;
  loadSale: (saleId: number) => Promise<SaleDetail>;
  refresh: () => Promise<void>;
}

const BillContext = createContext<BillStore | null>(null);

export function useBill(): BillStore {
  const store = useContext(BillContext);
  if (!store) throw new Error('ใช้ useBill นอก BillProvider ไม่ได้');
  return store;
}

export function BillProvider({ children }: { children: ReactNode }) {
  const [payload, setPayloadState] = useState<BillPayload>(EMPTY);
  const [cart, setCart] = useState<CartItem[]>([]);
  const [sale, setSale] = useState<SaleDetail | null>(null);
  const [pendingDiscount, setPendingDiscount] = useState<PendingDiscount | null>(null);

  const setPayload = useCallback((patch: Partial<BillPayload>) => {
    setPayloadState((p) => ({ ...p, ...patch }));
  }, []);

  const reset = useCallback(() => {
    setPayloadState(EMPTY);
    setCart([]);
    setSale(null);
    setPendingDiscount(null);
  }, []);

  /**
   * แตะเมนู = เพิ่มทีละ 1 · แตะซ้ำ = เพิ่มอีก 1
   * เหมือน handleProduct ของแอปเดิมเป๊ะ
   */
  const addProduct = useCallback((product: Product) => {
    setCart((items) => {
      const at = items.findIndex((i) => i.product.id === product.id && i.note === '');
      if (at >= 0) {
        const next = [...items];
        next[at] = { ...next[at]!, qty: next[at]!.qty + 1 };
        return next;
      }
      return [...items, { product, qty: 1, note: '' }];
    });
  }, []);

  const setQty = useCallback((index: number, qty: number) => {
    setCart((items) =>
      items.map((item, i) => (i === index ? { ...item, qty } : item)).filter((i) => i.qty > 0),
    );
  }, []);

  const setNote = useCallback((index: number, note: string) => {
    setCart((items) => items.map((item, i) => (i === index ? { ...item, note } : item)));
  }, []);

  const removeItem = useCallback((index: number) => {
    setCart((items) => items.filter((_, i) => i !== index));
  }, []);

  /**
   * ★ ปุ่มส่วนลดอยู่ในกริดเหมือนเมนู แต่ไม่ใช่สินค้า ★
   *
   * แอปเดิมกดแล้วแทรกบรรทัดสินค้าราคาติดลบลงบิล ที่นี่จำไว้ก่อน
   * แล้วส่งเป็นส่วนลดท้ายบิลจริง ๆ ตอนบันทึก — พนักงานกดเหมือนเดิมทุกอย่าง
   */
  const applyDiscountButton = useCallback((button: DiscountButton) => {
    setPendingDiscount({
      code: button.code,
      label: button.name,
      kind: button.kind,
      percent: button.percent,
      amountSatang: button.amountSatang,
    });
  }, []);

  const clearPendingDiscount = useCallback(() => setPendingDiscount(null), []);

  const loadSale = useCallback(async (saleId: number) => {
    const detail = await api.get<SaleDetail>(`/sales/${saleId}`);
    setSale(detail);
    setCart([]);
    setPendingDiscount(null);
    setPayloadState({
      saleId: detail.id,
      billNo: detail.billNo,
      billType: detail.billType,
      diningTableId: detail.diningTableId,
      tableName: '',
      guestCount: detail.guestCount,
      customerId: detail.customerId,
      customerName: '',
      customerPhone: '',
      memberPct: 0,
      deliveryChannelId: detail.deliveryChannelId,
      deliveryName: '',
      deliveryOrderRef: detail.deliveryOrderRef,
    });
    return detail;
  }, []);

  const refresh = useCallback(async () => {
    if (payload.saleId === null) return;
    const detail = await api.get<SaleDetail>(`/sales/${payload.saleId}`);
    setSale(detail);
    setCart([]);
  }, [payload.saleId]);

  const store = useMemo<BillStore>(
    () => ({
      payload, setPayload, sale,
      cart, addProduct, setQty, setNote, removeItem,
      pendingDiscount, applyDiscountButton, clearPendingDiscount,
      reset, loadSale, refresh,
    }),
    [payload, setPayload, sale, cart, addProduct, setQty, setNote, removeItem,
     pendingDiscount, applyDiscountButton, clearPendingDiscount, reset, loadSale, refresh],
  );

  return <BillContext.Provider value={store}>{children}</BillContext.Provider>;
}
